import {
  AbstractApiResponse,
  RbacGroup,
  RbacGroupWFullRoles,
  RbacGroupWRoles,
  RbacRole,
  RbacUser,
  RbacUserWFullRoles,
  RbacUserWRoles,
  SearchVidmUserRequest,
  SearchVidmUserResponse,
  VidmTaskResponse
} from 'vrlcm-gui-types';
import {
  extractVidmTaskErrors,
  extractVidmTaskSuccessData,
  Http,
  isVidmTaskJobSuccessful,
  vidmDataError
} from 'vrlcm-gui-utils';
import { EngineApi } from '../engine';
import { ProductApi } from '../lcops';
import { AuthznApiUri } from './uri';

interface UserCache {
  roles?: RbacRole[];
}

export class UserApi {
  private static userCache: UserCache = {};

  public static async getAllUsers() {
    const response = await Http.get<RbacUserWFullRoles[]>(
      AuthznApiUri.GET_ALL_USERS_URL
    );
    return response.data;
  }

  public static async getAllGroups() {
    const response = await Http.get<RbacGroupWFullRoles[]>(
      AuthznApiUri.GROUPS_URL
    );
    return response.data;
  }

  public static async getUser(vmid: string) {
    const response = await Http.get<RbacUserWFullRoles>(
      AuthznApiUri.USER_URL(vmid)
    );
    return response.data;
  }

  public static async getGroup(vmid: string) {
    const response = await Http.get<RbacGroupWFullRoles>(
      AuthznApiUri.GROUP_URL(vmid)
    );
    return response.data;
  }

  public static async addUser(user: RbacUserWRoles) {
    await Http.post(AuthznApiUri.USERS_URL, user);
  }

  public static async addGroup(group: RbacGroupWRoles) {
    await Http.post(AuthznApiUri.GROUPS_URL, group);
  }

  public static async addUsers(users: RbacUserWRoles[]) {
    await Promise.all(users.map(u => this.addUser(u)));
  }

  public static async addGroups(groups: RbacGroupWRoles[]) {
    await Promise.all(groups.map(g => this.addGroup(g)));
  }

  public static async deleteUser(vmid: string) {
    await Http.delete(AuthznApiUri.USER_URL(vmid));
  }

  public static async deleteGroup(vmid: string) {
    await Http.delete(AuthznApiUri.GROUP_URL(vmid));
  }

  public static async updateUser(vmid: string, newRoleVmids: string[]) {
    const user = await this.getUser(vmid);
    const response = await Http.put(AuthznApiUri.USER_URL(vmid), {
      ...user,
      mappedRoles: newRoleVmids
    });
    return response.data;
  }

  public static async updateGroup(vmid: string, newRoleVmids: string[]) {
    const group = await this.getGroup(vmid);
    const response = await Http.put(AuthznApiUri.GROUP_URL(vmid), {
      ...group,
      mappedRoles: newRoleVmids
    });
    return response.data;
  }

  public static searchUsersFromVidm(
    data: SearchVidmUserRequest,
    tenantName?: string,
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse<SearchVidmUserResponse>> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    return ProductApi.getVidmData(tenantName).then(
      vidmData => {
        return EngineApi.triggerJobAndReturnResponse<
          SearchVidmUserRequest,
          VidmTaskResponse<SearchVidmUserResponse>
        >(
          AuthznApiUri.SEARCH_VIDM_USER_GROUP_URL,
          { ...vidmData, ...data },
          cf => cancelFunctions.push(cf)
        ).then(res => ({
          success: isVidmTaskJobSuccessful(res.data),
          data: extractVidmTaskSuccessData<SearchVidmUserResponse>(res.data),
          errors: extractVidmTaskErrors(res.data),
          when: new Date()
        }));
      },
      err => ({
        success: false,
        errors: [vidmDataError(err)],
        when: new Date()
      })
    );
  }

  public static async fetchRoles(useCache = false) {
    const { roles } = this.userCache;
    if (useCache && roles) {
      return roles;
    }

    const response = await Http.get<RbacRole[]>(AuthznApiUri.GET_ALL_ROLES_URL);
    this.userCache.roles = response.data.filter(
      r => r.roleName.toLowerCase().indexOf('lcm admin') === -1
    );
    return this.userCache.roles;
  }

  public static async userHasRole(user: RbacUser) {
    try {
      const response = await Http.get<RbacUserWFullRoles>(
        AuthznApiUri.GET_USER_BY_PROVIDER_ID_URL(user.providerIdentifier)
      );
      return (
        response.data &&
        response.data.providerIdentifier === user.providerIdentifier
      );
    } catch (e) {
      return false;
    }
  }

  public static async groupHasRole(group: RbacGroup) {
    try {
      const response = await Http.get<RbacGroupWFullRoles>(
        AuthznApiUri.GET_GROUP_BY_PROVIDER_ID_URL(group.providerIdentifier)
      );
      return (
        response.data &&
        response.data.providerIdentifier === group.providerIdentifier
      );
    } catch (e) {
      return false;
    }
  }
}
